import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import checkWithinPolygon from 'point-in-geopolygon';
import { webMercatorToGeographic } from '@rsos/maps/providers/esri/geometry/support/webMercatorToGeographic';
import { createGraphicsLayer } from '@rsos/maps/providers/esri/graphics/layers';
import {
  createSketchWidget,
  SKETCH_WIDGET_TITLE,
} from '@rsos/maps/providers/esri/widgets/createSketchWidget';
import { callQueueSelector } from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import checkEsriMapAccess from '@rsos/utils/checkEsriMapAccess';
import checkEsriSketchWidgetAccessSelector from '@rsos/utils/checkEsriSketchWidgetAccessSelector';
import { captureException } from '@rsos/utils/sentry';
import usePortalType from '@rsos/utils/usePortalType';
import { getMostRecentLoc } from '../utils/getMostRecentLoc';

// Manually position the sketch widget UI on the map so that it doesn't overlap other UI components.
// https://developers.arcgis.com/javascript/latest/api-reference/esri-views-ui-UI.html#add
const POSITION_MANUAL = 'manual';

const SKETCH_GRAPHICS_LAYER_TITLE = 'Sketch Graphics Layer';

/**
 * Similar version to the isLocationInPolygon() in 'libs/components/capstone/common/Map/utils/spatialLayersHelpers.js'
 * This util function utilizes the polygon() method from the checkWithinPolygon package.
 *
 * Check if a location's coordinates are inside the drawn graphic polygon.
 * @param {Array} polygon - The GeoJSON Polygon is defined by an array of its vertex.
 *   The first vertex in the array and the last one MUST be the same point.
 * @param {Array} coordinates - The coordinates of the 911 or alert event, the array must be in
 *   [longitude, latitude].
 * @returns Boolean
 */
const areCoordinatesInsidePolygon = ({ polygon, coordinates }) =>
  polygon && coordinates
    ? checkWithinPolygon.polygon(polygon, coordinates)
    : null;

/**
 * Check if 911 events in the queue are inside the drawn graphic polygon.
 * @param {Object} callQueue - 911 events queue
 * @param {Array} polygon - The GeoJSON Polygon is defined by an array of its vertex.
 *   The first vertex in the array and the last one MUST be the same point.
 * @returns Object
 */
const getCallsInsidePolygon = ({ callQueue, polygon }) => {
  const callsInsidePolygon = Object.entries(callQueue)
    // eslint-disable-next-line no-unused-vars
    .filter(([callerID, callData]) => {
      const mostRecentLocation = getMostRecentLoc(callData);

      const coordinates = [
        parseFloat(mostRecentLocation?.longitude),
        parseFloat(mostRecentLocation?.latitude),
      ];

      return areCoordinatesInsidePolygon({
        polygon,
        coordinates,
      });
    })
    .map(([callerID, callData]) => {
      return {
        [callerID]: callData,
      };
    });

  return callsInsidePolygon.length ? Object.assign(...callsInsidePolygon) : {};
};

const useEsriSketchWidget = map => {
  const { isPremiumUser } = usePortalType();

  const hasEsriMapAccess = useSelector(state =>
    checkEsriMapAccess({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const hasSketchWidgetAccess =
    useSelector(state =>
      checkEsriSketchWidgetAccessSelector({
        psaps: state.psaps,
        sinatra: state.sinatra,
      }),
    ) &&
    hasEsriMapAccess &&
    isPremiumUser;

  const mapLayers = map?.mapInstance?.map?.layers;

  const mapView = map?.mapInstance?.view;

  // Find the sketch widget using the specified id
  const sketchWidget = mapView?.ui?.find(SKETCH_WIDGET_TITLE);

  // const callQueue = useSelector(state =>
  //   callQueueSelector(state.emergencyCalls.callQueue),
  // );

  // const selectedCallerID = useSelector(
  //   state => state.emergencyCalls.selectedCallerID,
  // );

  // const selectedCallMostRecentLocation = getMostRecentLoc(
  //   callQueue[selectedCallerID],
  // );

  // const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);

  // const isAllView =
  //   (!selectedCallerID || !selectedCallMostRecentLocation) && !selectedAlertID;

  // const [sketchGraphics, setSketchGraphics] = useState([]);

  // const [deletedGraphics, setDeletedGraphics] = useState([]);

  // const [polygons, setPolygons] = useState([]);

  // const [callsInsidePolygons, setCallsInsidePolygons] = useState({});

  // const numCallsInsidePolygons = useMemo(() => {
  //   return Object.keys(callsInsidePolygons).length;
  // }, [callsInsidePolygons]);

  // Create and toggle the visibility of the sketch widget.
  useEffect(() => {
    if (hasSketchWidgetAccess && mapLayers && mapView) {
      if (sketchWidget) {
        // If the sketch widget already exists, set the visibility to true
        sketchWidget.visible = true;
      } else {
        const layerConfigs = {
          title: SKETCH_GRAPHICS_LAYER_TITLE,
        };

        try {
          (async () => {
            const graphicsLayer = await createGraphicsLayer({
              configs: layerConfigs,
            });

            mapLayers.push(graphicsLayer);

            const sketchWidget = createSketchWidget({
              graphicsLayer,
              mapView,
            });

            mapView.ui.add(sketchWidget, POSITION_MANUAL);
          })();
        } catch (error) {
          captureException(error);
        }
      }
    }
  }, [hasSketchWidgetAccess, mapLayers, mapView, sketchWidget]);

  // Add new graphics created by the sketch widget.
  // const onGraphicCreate = useCallback(async event => {
  //   // Check if the create event's state has changed to complete indicating the graphic create
  //   // operation is completed.
  //   if (event.state === 'complete') {
  //     const { geometry, uid } = event.graphic;

  //     // Once a graphic is drawn on the map, the geometry is in Web Mercator units
  //     // (wkid: 3857), it needs to be converted to geographic units (wkid: 4326) in order
  //     // to be used.
  //     const convertedGeometry = await webMercatorToGeographic(geometry);

  //     const newGraphic = {
  //       geometry: convertedGeometry?.rings,
  //       isUpdated: false,
  //       uid,
  //     };

  //     // TODO: double check sketch graphics on create complete
  //     setSketchGraphics(prev => [...prev, newGraphic]);
  //     // setSketchGraphics([...sketchGraphics, newGraphic]);
  //   }
  // }, []);

  // Update existing graphics created by the sketch widget.
  // const onGraphicsUpdate = useCallback(
  //   event => {
  //     if (event.state === 'complete') {
  //       // The update event can update multiple graphics at once depending on selection.
  //       event.graphics.forEach(async graphic => {
  //         const { geometry, uid } = graphic;

  //         const convertedGeometry = await webMercatorToGeographic(geometry);

  //         const filteredGraphics = sketchGraphics.map(graphic => {
  //           if (graphic.uid === uid) {
  //             return {
  //               ...graphic,
  //               geometry: convertedGeometry?.rings,
  //               isUpdated: true,
  //             };
  //           } else {
  //             return graphic;
  //           }
  //         });

  //         setSketchGraphics([...filteredGraphics]);
  //       });
  //     }
  //   },
  //   [sketchGraphics],
  // );

  // Delete existing graphics created by the sketch widget.
  // const onGraphicsDelete = useCallback(
  //   event => {
  //     // The delete event can delete multiple graphics at once depending on selection.
  //     event.graphics.forEach(graphic => {
  //       setDeletedGraphics([...deletedGraphics, graphic]);
  //     });
  //   },
  //   [deletedGraphics],
  // );

  // Watches for sketch event handlers on the map instance.
  // useEffect(() => {
  //   if (sketchWidget) {
  //     // Registers an event handler on the instance.
  //     // https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Sketch.html#events-summary
  //     sketchWidget.on('create', event => onGraphicCreate(event));
  //     sketchWidget.on('update', event => onGraphicsUpdate(event));
  //     sketchWidget.on('delete', event => onGraphicsDelete(event));
  //   }
  // }, [onGraphicCreate, onGraphicsDelete, onGraphicsUpdate, sketchWidget]);

  // Watches for any updated or deleted graphics on the map instance.
  // useEffect(() => {
  //   if (sketchGraphics.length) {
  //     let filteredGraphics = [];

  //     if (deletedGraphics.length) {
  //       filteredGraphics = sketchGraphics.filter(
  //         sketchGraphic =>
  //           !deletedGraphics.some(
  //             deletedGraphic => sketchGraphic.uid === deletedGraphic.uid,
  //           ),
  //       );
  //     } else {
  //       filteredGraphics = sketchGraphics;
  //     }

  //     setPolygons([...filteredGraphics]);
  //   }
  // }, [deletedGraphics, sketchGraphics]);

  // Assign 911 events to the `callsInsidePolygons` state hook.
  // useEffect(() => {
  //   if (isAllView && callQueue && polygons) {
  //     let filteredCalls = {};

  //     polygons.forEach(graphic => {
  //       const calls = getCallsInsidePolygon({
  //         callQueue,
  //         polygon: graphic?.geometry,
  //       });

  //       filteredCalls = { ...filteredCalls, ...calls };
  //     });

  //     setCallsInsidePolygons({ ...filteredCalls });
  //   }
  // }, [isAllView, callQueue, polygons]);

  // useEffect(() => {
  //   // eslint-disable-next-line no-console
  //   console.log(
  //     'Number of 911 events inside drawn graphics',
  //     numCallsInsidePolygons,
  //   );
  // }, [numCallsInsidePolygons]);
};

export default useEsriSketchWidget;
